import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavbar } from '../../contexts/navigation/NavbarContext';
import { useSEO } from '../../hooks/useSEO';
import VideoSection from './video-section/VideoSection';
import PortfolioSection from './portfolio-section/PortfolioSection';
import './HomePage.scss';

const HomePage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // SEO optimization
  useSEO({
    title: 'ALINA POLONEYEVA',
    description: 'VISUAL CREATOR / MODEL / FASHION WEEK PRESENCE',
    keywords: 'Alina Poloneyeva, Ukrainian model, visual creator, fashion content creator, model Warsaw, model Paris, fashion week, editorial modeling, content creation, Ukraine model, Ukrainian fashion',
    ogTitle: 'ALINA POLONEYEVA',
    ogDescription: 'VISUAL CREATOR / MODEL / FASHION WEEK PRESENCE',
    ogImage: 'https://alinapoloneyeva.com/images/about/about.jpg',
    ogUrl: 'https://alinapoloneyeva.com/',
    canonical: 'https://alinapoloneyeva.com/',
  });

  // Determine initial section based on pathname
  const getInitialSection = useCallback(() => {
    return location.pathname === '/portfolio' ? 1 : 0;
  }, [location.pathname]);

  // Initialize activeSection synchronously based on pathname to avoid flash
  const [activeSection, setActiveSection] = useState(() => {
    return location.pathname === '/portfolio' ? 1 : 0;
  });
  const { setHideNav, setActiveHomeSection, setGoToVideoSection, setGoToPortfolioSection } = useNavbar();
  const isTransitioning = useRef(false);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([null, null]);
  const isNavigatingViaScroll = useRef(false);
  const [portfolioMounted, setPortfolioMounted] = useState(() => location.pathname === '/portfolio');

  // Immediately set activeHomeSection on mount to ensure NavBar has correct state
  useEffect(() => {
    const initialSection = location.pathname === '/portfolio' ? 1 : 0;
    setActiveHomeSection(initialSection);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync activeSection with pathname changes (from browser back/forward or direct navigation)
  useEffect(() => {
    if (!isNavigatingViaScroll.current) {
      const targetSection = getInitialSection();
      if (targetSection !== activeSection) {
        setActiveSection(targetSection);
      }
    }
    isNavigatingViaScroll.current = false;
  }, [location.pathname, getInitialSection, activeSection]);

  // Keep navbar visible on all sections
  useEffect(() => {
    setHideNav(false);
  }, [setHideNav]);

  // Update active home section in context when activeSection changes
  useEffect(() => {
    setActiveHomeSection(activeSection);
    // Don't reset to null on unmount - prevents NavBar color flash
  }, [activeSection, setActiveHomeSection]);

  // Navigation function for sections
  const goToSection = useCallback((section: number) => {
    if (section === activeSection || isTransitioning.current) return;
    if (section < 0 || section > 1) return; // Only 2 sections now

    // Mount portfolio section when navigating to it
    if (section === 1 && !portfolioMounted) {
      setPortfolioMounted(true);
    }

    isTransitioning.current = true;
    isNavigatingViaScroll.current = true;
    setActiveSection(section);

    // Update URL based on section
    const newPath = section === 1 ? '/portfolio' : '/';
    if (location.pathname !== newPath) {
      navigate(newPath, { replace: false });
    }

    // Reset scroll position for scrollable sections
    if (section > 0) {
      requestAnimationFrame(() => {
        const sectionRef = sectionRefs.current[section];
        if (sectionRef) {
          sectionRef.scrollTop = 0;
        }
      });
    }

    // Allow next transition after animation + cooldown
    setTimeout(() => {
      isTransitioning.current = false;
    }, 650);
  }, [activeSection, location.pathname, navigate, portfolioMounted]);

  // Set goToVideoSection callback in context
  useEffect(() => {
    setGoToVideoSection(() => () => {
      goToSection(0);
    });
    return () => {
      setGoToVideoSection(null);
    };
  }, [setGoToVideoSection, goToSection]);

  // Set goToPortfolioSection callback in context
  useEffect(() => {
    setGoToPortfolioSection(() => () => {
      goToSection(1);
    });
    return () => {
      setGoToPortfolioSection(null);
    };
  }, [setGoToPortfolioSection, goToSection]);

  useEffect(() => {
    let lastWheelTime = 0;
    const WHEEL_COOLDOWN = 500; // Further reduced cooldown for faster scrolling

    const handleWheel = (e: WheelEvent) => {
      const now = Date.now();

      // Prevent multiple rapid transitions
      if (isTransitioning.current || now - lastWheelTime < WHEEL_COOLDOWN) {
        // Allow normal scrolling within section if not transitioning
        if (!isTransitioning.current && activeSection > 0) {
          const currentSectionRef = sectionRefs.current[activeSection];
          if (currentSectionRef) {
            const scrollTop = currentSectionRef.scrollTop;
            const scrollHeight = currentSectionRef.scrollHeight;
            const clientHeight = currentSectionRef.clientHeight;
            const isAtTop = scrollTop <= 5;
            const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 5;

            // Allow scrolling if not at boundaries
            if ((e.deltaY > 0 && !isAtBottom) || (e.deltaY < 0 && !isAtTop)) {
              return;
            }
          }
        }
        e.preventDefault();
        return;
      }

      const delta = e.deltaY;
      const SCROLL_THRESHOLD = 20; // Further reduced threshold for faster scrolling

      // Ignore small scroll amounts
      if (Math.abs(delta) < SCROLL_THRESHOLD) {
        return;
      }

      // Video section - scroll down to go to next section
      if (activeSection === 0 && delta > 0) {
        e.preventDefault();
        lastWheelTime = now;
        goToSection(1);
        return;
      }

      // Scrollable sections
      if (activeSection > 0) {
        const currentSectionRef = sectionRefs.current[activeSection];
        if (!currentSectionRef) return;

        const scrollTop = currentSectionRef.scrollTop;
        const scrollHeight = currentSectionRef.scrollHeight;
        const clientHeight = currentSectionRef.clientHeight;
        const isAtTop = scrollTop <= 5;
        const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 5;

        // Scroll UP - go to previous section if at top
        if (delta < 0 && isAtTop && activeSection > 0) {
          e.preventDefault();
          lastWheelTime = now;
          goToSection(activeSection - 1);
          return;
        }

        // Scroll DOWN - go to next section if at bottom
        if (delta > 0 && isAtBottom && activeSection < 1) { // Only 2 sections now
          e.preventDefault();
          lastWheelTime = now;
          goToSection(activeSection + 1);
          return;
        }

        // Allow normal scrolling within the section
        return;
      }
    };

    let touchStartY = 0;
    let isTouching = false;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
      isTouching = true;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (isTransitioning.current || !isTouching) {
        return;
      }

      const touchCurrentY = e.touches[0].clientY;
      const deltaY = touchStartY - touchCurrentY;
      const threshold = 80; // Increased threshold for more intentional swipes
      const currentSectionRef = sectionRefs.current[activeSection];

      // Video section - swipe up to go to next section
      if (activeSection === 0 && deltaY > threshold) {
        e.preventDefault();
        isTouching = false;
        goToSection(1);
        return;
      }

      // Scrollable sections
      if (activeSection > 0 && currentSectionRef) {
        const scrollTop = currentSectionRef.scrollTop;
        const scrollHeight = currentSectionRef.scrollHeight;
        const clientHeight = currentSectionRef.clientHeight;
        const isAtTop = scrollTop <= 5;
        const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 4;

        // Swipe UP (deltaY > 0) - check if at bottom
        if (deltaY > threshold && isAtBottom && activeSection < 1) { // Only 2 sections now
          e.preventDefault();
          isTouching = false;
          goToSection(activeSection + 1);
          return;
        }

        // Swipe DOWN (deltaY < 0) - check if at top
        if (deltaY < -threshold && isAtTop && activeSection > 0) {
          e.preventDefault();
          isTouching = false;
          goToSection(activeSection - 1);
          return;
        }
      }
    };

    const handleTouchEnd = () => {
      isTouching = false;
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [activeSection, goToSection]);

  return (
    <div className="home-page-slider">
      <div
        className="home-page-slider__container"
        style={{
          transform: `translateY(-${activeSection * 100}vh)`,
        }}
      >
        <div className="home-page-slider__slide">
          <VideoSection />
        </div>
        <div className="home-page-slider__slide">
          {portfolioMounted && (
            <PortfolioSection ref={(el) => { sectionRefs.current[1] = el; }} />
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePage;
